/**
 * script_favorites-page.js
 *
 * お気に入りページ（/pages/favorites）専用のレンダラー。
 * roi:favorites:v2 の localStorage を読み込み、
 * /products/{handle}.js から商品情報を取得して
 * products-grid と同じカード構造を DOM で生成する。
 *
 * セキュリティ方針:
 *   - DOM 操作は createElement + textContent のみ（innerHTML は一切使わない）
 *   - href に使う URL は /products/ 始まりの相対パスのみ許可する
 *   - localStorage の値はすべてバリデーションしてから使用する
 *   - 商品取得の同時接続数は FETCH_CONCURRENCY 以下に制限する
 *   - 最大表示件数は MAX_ITEMS 以下に制限する
 *
 * script_cart-actions.js との連携:
 *   - このファイルは script_cart-actions.js より後に実行される（defer の順序保証）
 *   - カード生成後に 'shopify:section:load' を dispatch して
 *     script_cart-actions.js の syncAll() を起動しカート状態・お気に入り状態を確定させる
 *   - script_cart-actions.js からの 'roi:favorites:changed' CustomEvent を受け取り
 *     お気に入り変更時にページを再描画する
 */

// このファイル内のすべての変数・関数を IIFE でスコープに閉じ込めて
// script_cart-actions.js などのグローバル変数と衝突しないようにする
(function () {

  /* ============================================================
     定数
     ============================================================ */

  /** お気に入りデータを読み書きする localStorage のキー（script_cart-actions.js と共通） */
  const FAVORITES_KEY = 'roi:favorites:v2';

  /** 1回の描画で表示する最大件数（過剰リクエスト防止） */
  const MAX_ITEMS = 50;

  /** 商品 JSON を同時に取得する最大接続数（サーバー負荷抑制） */
  const FETCH_CONCURRENCY = 4;

  /** ハートアイコンの SVG パスデータ（products-grid.liquid と同じもの） */
  const HEART_SVG_PATH =
    'M442.9 144C415.6 144 389.9 157.1 373.9 179.2L339.5 226.8C335 233 327.8 236.7 320.1 236.7C312.4 236.7 305.2 233 300.7 226.8L266.3 179.2C250.3 157.1 224.6 144 197.3 144C150.3 144 112.2 182.1 112.2 229.1C112.2 279 144.2 327.5 180.3 371.4C221.4 421.4 271.7 465.4 306.2 491.7C309.4 494.1 314.1 495.9 320.2 495.9C326.3 495.9 331 494.1 334.2 491.7C368.7 465.4 419 421.3 460.1 371.4C496.3 327.5 528.2 279 528.2 229.1C528.2 182.1 490.1 144 443.1 144zM335 151.1C360 116.5 400.2 96 442.9 96C516.4 96 576 155.6 576 229.1C576 297.7 533.1 358 496.9 401.9C452.8 455.5 399.6 502 363.1 529.8C350.8 539.2 335.6 543.9 320 543.9C304.4 543.9 289.2 539.2 276.9 529.8C240.4 502 187.2 455.5 143.1 402C106.9 358.1 64 297.7 64 229.1C64 155.6 123.6 96 197.1 96C239.8 96 280 116.5 305 151.1L320 171.8L335 151.1z';

  /** 商品カード画像のレスポンシブ配信設定 */
  const PRODUCT_IMAGE_WIDTHS = [180, 260, 360, 480, 600];
  const PRODUCT_IMAGE_SIZES = '(min-width: 1120px) 16.67vw, (min-width: 720px) 25vw, 33.33vw';

  /** Shopify CDN URLへ表示幅を安全に指定する */
  function withShopifyImageWidth(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  /* ============================================================
     商品データのインメモリキャッシュ
     ============================================================ */

  /**
   * handle をキーとして商品 JSON を保持するキャッシュ。
   * お気に入り解除後の再描画でも再フェッチを避けるために使用する。
   * ページのリロードまで有効。
   */
  const productCache = new Map();

  /* ============================================================
     バリデーション
     ============================================================ */

  /** 商品ID・バリアントIDは数字文字列のみ許可する */
  function isValidId(val) {
    return typeof val === 'string' && /^\d+$/.test(val.trim());
  }

  /** ハンドルは小文字英数字とハイフンのみ許可する（Shopify の仕様に合わせる） */
  function isValidHandle(val) {
    return typeof val === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(val.trim());
  }

  /* ============================================================
     localStorage からお気に入り一覧を取得する
     ============================================================ */

  /**
   * localStorage からバリデーション済みのお気に入りエントリ配列を返す。
   * 不正な値・破損データは無視して空配列を返す。
   * 最大 MAX_ITEMS 件に絞る（超過分は古いものから切り捨てる）。
   */
  function getFavoritesV2() {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter((entry) => {
          if (!entry || typeof entry !== 'object') return false;
          const id = String(entry.id ?? '');
          const handle = String(entry.handle ?? '');
          const url = String(entry.url ?? '');
          if (!isValidId(id) || !isValidHandle(handle)) return false;
          // URL は /products/ 始まりの相対パスのみ（javascript: 等を排除）
          if (!/^\/products\/[a-zA-Z0-9][a-zA-Z0-9\-_]*/.test(url)) return false;
          return true;
        })
        .slice(0, MAX_ITEMS);
    } catch {
      // localStorage が使えない環境（プライベートブラウズなど）は空を返す
      return [];
    }
  }

  /* ============================================================
     Shopify.formatMoney のフォールバック定義
     ============================================================ */

  /**
   * Shopify.formatMoney が存在しない場合に最小限の実装を定義する。
   * theme.liquid などで既に読み込まれている場合はそちらを優先し、上書きしない。
   *
   * 核心は「cents ÷ 100 = 表示金額」。
   * /products/{handle}.js の price は cents 単位のため、
   * ÷100 することで正しい表示価格になる（0が2つ多い問題の根本解消）。
   *
   * サポートするプレースホルダー（JPY を含む主要書式）:
   *   {{amount}}                                  → 小数2桁 例: "1,980.00"
   *   {{amount_no_decimals}}                      → 小数なし 例: "1,980"  ← JPY はこれが多い
   *   {{amount_with_comma_separator}}             → ピリオド区切り 例: "1.980,00"
   *   {{amount_no_decimals_with_comma_separator}} → ピリオド区切り小数なし 例: "1.980"
   *   {{amount_with_apostrophe_separator}}        → アポストロフィ区切り 例: "1'980.00"
   */
  function ensureShopifyFormatMoney() {
    // 既に存在する場合は何もしない（theme.liquid の実装を優先する）
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return;

    window.Shopify = window.Shopify || {};

    window.Shopify.formatMoney = function (cents, format) {
      // 文字列で渡された場合（小数点を除去して整数に統一する）
      if (typeof cents === 'string') cents = cents.replace('.', '');

      // プレースホルダー {{xxxxx}} を検出する正規表現
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var match = (format || '').match(placeholderRegex);

      // プレースホルダーが読み取れない場合は cents をそのまま返す（安全側に倒す）
      if (!match) return String(cents);

      /**
       * cents を 100 で割り、指定の桁数と区切り文字でフォーマットする。
       * ÷100 がここで行われるため、表示金額に 0 が2つ多い問題が解消する。
       */
      function fmt(precision, thousands, decimal) {
        thousands = thousands !== undefined ? thousands : ',';
        decimal   = decimal   !== undefined ? decimal   : '.';
        var n     = (Math.abs(Number(cents)) / 100).toFixed(precision);
        var parts = n.split('.');
        // 3桁ごとに区切り文字を挿入する
        parts[0]  = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
        return precision > 0 ? parts.join(decimal) : parts[0];
      }

      var value = '';
      switch (match[1]) {
        case 'amount':
          value = fmt(2); break;
        case 'amount_no_decimals':
          value = fmt(0); break;
        case 'amount_with_comma_separator':
          value = fmt(2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator':
          value = fmt(0, '.', ','); break;
        case 'amount_with_apostrophe_separator':
          value = fmt(2, "'", '.'); break;
        default:
          // 未知のプレースホルダーは小数なしで処理する
          value = fmt(0);
      }

      // {{xxxxx}} の部分をフォーマット結果で置き換えた文字列を返す
      return format.replace(placeholderRegex, value);
    };
  }

  /* ============================================================
     money_without_currency_format を data 属性から安全に取得する
     ============================================================ */

  /**
   * favorites-page セクションの data-money-without-currency-format 属性から
   * Shopify.formatMoney に渡す書式文字列を取得する。
   *
   * Liquid の | json は {{amount_no_decimals}} を Unicode エスケープ
   * （\u007B\u007Bamount_no_decimals\u007D\u007D）で出力する場合がある。
   * JSON.parse を経由することで、どちらの出力形式でも元の文字列に確実に復元できる。
   *
   * またLiquidの shop.money_without_currency_format が nil のとき
   * | json は JSON の "null" を出力し、JSON.parse すると JavaScript の null になる。
   * そのケースもここでデフォルト値に吸収する。
   *
   * フォールバック優先順:
   *   1. JSON.parse 結果が {{...}} を含む文字列 → それを採用
   *   2. それ以外（null・非文字列・プレースホルダー無し）→ デフォルトに落とす
   *   3. JSON.parse 自体が失敗した場合 → raw をそのまま使う（念のための互換）
   *   4. 属性がない・空の場合 → JPY のデフォルト値
   */
  function getMoneyFormat() {
    var FALLBACK = '{{amount_no_decimals}}';
    var section = document.querySelector('.favorites-page');
    var raw = section && section.dataset.moneyWithoutCurrencyFormat;

    // 属性自体がない・空の場合はデフォルトへ
    if (!raw) return FALLBACK;

    // JSON.parse で Unicode エスケープを復元する（例: \u007B → {）
    try {
      var parsed = JSON.parse(raw);

      // null・数値・真偽値などはすべてデフォルトへ落とす
      if (typeof parsed !== 'string' || parsed.length === 0) return FALLBACK;

      // {{...}} プレースホルダーが含まれていない場合、formatMoney が String(cents) に
      // フォールバックして 0増殖が再発するためデフォルトへ落とす
      if (!/\{\{/.test(parsed)) return FALLBACK;

      return parsed;
    } catch {
      // JSON.parse に失敗した場合は raw をそのまま使う（旧形式との互換）
      // ただし raw が "null" 文字列のときはデフォルトへ
      if (raw === 'null' || !/\{\{/.test(raw)) return FALLBACK;
      return raw;
    }
  }

  /* ============================================================
     価格フォーマット（cents → 表示文字列）
     ============================================================ */

  /**
   * /products/{handle}.js から取得した cents 単位の価格を
   * Shopify.formatMoney 経由で通貨記号なしの表示文字列に変換する。
   *
   * 旧 formatPrice() とは異なり、cents ÷ 100 が内部で行われるため
   * 0が2つ多い問題が解消される。
   * カンマ区切りは Shopify.formatMoney の fmt() 内で処理される。
   *
   * @param {number} cents - /products/{handle}.js の price または compare_at_price
   * @returns {string} 例: 198000 (cents) → "1,980"
   */
  function formatMoneyCents(cents) {
    // Shopify.formatMoney がなければフォールバック実装を用意してから呼ぶ
    ensureShopifyFormatMoney();
    return window.Shopify.formatMoney(cents, getMoneyFormat());
  }

  /* ============================================================
     並列数を制限しながら非同期処理を実行するユーティリティ
     ============================================================ */

  /**
   * tasks（引数なしの非同期関数の配列）を最大 concurrency 件ずつ
   * 並列実行し、全件完了後に結果の配列を返す。
   * 同時接続数を制限してサーバー負荷を抑える。
   *
   * @param {Array<() => Promise<any>>} tasks
   * @param {number} concurrency
   * @returns {Promise<Array<any>>}
   */
  async function runWithConcurrency(tasks, concurrency) {
    const results = new Array(tasks.length);
    let nextIndex = 0;

    async function runNext() {
      if (nextIndex >= tasks.length) return;
      const i = nextIndex++;
      results[i] = await tasks[i]();
      await runNext();
    }

    // 最大 concurrency 本のワーカーを同時に走らせる
    const workers = Array.from(
      { length: Math.min(concurrency, tasks.length) },
      runNext
    );
    await Promise.all(workers);
    return results;
  }

  /* ============================================================
     /products/{handle}.js から商品情報を取得する
     ============================================================ */

  /**
   * Shopify の商品 JSON エンドポイントから商品オブジェクトを取得する。
   * キャッシュに存在すれば再フェッチしない。
   * 通信失敗・不正レスポンスの場合は null を返す（呼び出し元がスキップする）。
   *
   * @param {string} handle - バリデーション済みの商品ハンドル
   * @returns {Promise<Object|null>}
   */
  async function fetchProductByHandle(handle) {
    // キャッシュヒット: ネットワークを使わず即返す
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const encodedHandle = encodeURIComponent(handle);

      // 商品情報と launch_date を並列で取得する
      // launch_date は /products/{handle}?view=launch-date で返る JSON 文字列（設定なしは null）
      const [productRes, launchRes] = await Promise.all([
        fetch(`/products/${encodedHandle}.js`, {
          headers: { Accept: 'application/json' },
        }),
        fetch(`/products/${encodedHandle}?view=launch-date`).catch(() => null),
      ]);

      if (!productRes.ok) return null;

      const product = await productRes.json();

      // 最低限必要なフィールドが存在するか確認してから使用する
      if (
        !product ||
        typeof product.id !== 'number' ||
        !Array.isArray(product.variants) ||
        product.variants.length === 0
      ) {
        return null;
      }

      // launch_date を product に付与する（取得失敗・未設定・文字列以外は null）
      // テンプレートが "2024-..." のような JSON 文字列を返すことを想定している。
      // オブジェクト等が返った場合も isComingSoon() に渡る前にここで弾く。
      try {
        const parsed = launchRes && launchRes.ok
          ? JSON.parse(await launchRes.text())
          : null;
        product.launchDate = typeof parsed === 'string' ? parsed : null;
      } catch {
        product.launchDate = null;
      }

      // キャッシュに保存して次回の再描画でフェッチを省略する
      productCache.set(handle, product);
      return product;
    } catch {
      // ネットワークエラーは null で吸収する（ページ全体を壊さない）
      return null;
    }
  }

  /* ============================================================
     商品カードの DOM を生成する
     ============================================================ */

  /**
   * /products/{handle}.js のレスポンスから products-grid.liquid と
   * 同じ構造の <li class="products-grid__item"> を生成して返す。
   *
   * セキュリティ: innerHTML は一切使わない。
   * すべての文字列は textContent または createElement 経由で設定する。
   * href は /products/ 始まりの相対パスのみ使用する。
   *
   * @param {Object} product - /products/{handle}.js のレスポンス
   * @param {string} cartAddUrl - カート追加エンドポイント（Liquid から渡す）
   * @param {string} cartUrl - カートページ URL（Liquid から渡す）
   * @returns {HTMLElement|null}
   */
  function buildProductCard(product, cartAddUrl, cartUrl) {
    // 在庫あり優先で最初のバリアントを選ぶ（products-grid.liquid の動作と揃える）
    const variant =
      product.variants.find((v) => v.available) || product.variants[0];
    if (!variant) return null;

    const productId = String(product.id);
    const variantId = String(variant.id);
    const available = Boolean(variant.available);

    // handle から正規の商品 URL を組み立てる（コンテキスト依存の product.url を使わない）
    const productUrl = `/products/${product.handle}`;

    /* ---- <li class="products-grid__item"> ---- */
    const li = document.createElement('li');
    li.className = 'products-grid__item';
    // script_cart-actions.js が参照するデータ属性をすべて付ける
    li.dataset.productId = productId;
    li.dataset.productHandle = product.handle;
    li.dataset.variantId = variantId;
    li.dataset.variantAvailable = String(available);
    // launch_date が設定されていれば isComingSoon() が COMING SOON 判定に使う
    li.dataset.launchDate = product.launchDate || '';

    /* ---- <a class="products-grid__link"> ---- */
    const link = document.createElement('a');
    link.href = productUrl; // バリデーション済み相対パス
    link.className = 'products-grid__link';

    /* ---- 商品画像 ---- */
    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'products-grid__image';
      img.src = withShopifyImageWidth(product.featured_image, 360);
      img.srcset = PRODUCT_IMAGE_WIDTHS
        .map((width) => `${withShopifyImageWidth(product.featured_image, width)} ${width}w`)
        .join(', ');
      img.sizes = PRODUCT_IMAGE_SIZES;
      // alt は textContent と同等の安全な属性
      img.alt = product.title;
      img.width = 300;
      img.height = 300;
      img.loading = 'lazy';
      img.decoding = 'async';
      link.appendChild(img);
    }

    /* ---- 商品タイトル ---- */
    const titleEl = document.createElement('h3');
    titleEl.className = 'products-grid__title';
    titleEl.textContent = product.title; // XSS 対策: textContent を使用
    link.appendChild(titleEl);

    /* ---- 価格 ---- */
    const priceP = document.createElement('p');
    priceP.className = 'products-grid__price';

    // variant レベルの compare_at_price を優先し、なければ product レベルを使う
    const rawPrice = Number(variant.price ?? product.price);
    const rawCompare = variant.compare_at_price
      ? Number(variant.compare_at_price)
      : Number(product.compare_at_price) || 0;
    const isOnSale = rawCompare > rawPrice;

    if (isOnSale) {
      /* セール価格: 割引後価格 + 元値 */
      const saleSpan = document.createElement('span');
      saleSpan.className = 'products-grid__price--sale';
      saleSpan.dataset.price = String(rawPrice);
      saleSpan.dataset.compareAtPrice = String(rawCompare);
      // cents → 表示金額に変換（÷100 + カンマ区切り）
      saleSpan.appendChild(document.createTextNode(formatMoneyCents(rawPrice)));
      // Liquid の {{ price }} と税ラベルの間には改行由来の空白が入るが、
      // JS生成時は空白が0になるため CSS の負マージンで重なる。
      // NBSP を挿入することで products-grid.liquid と同じ間隔を再現する。
      saleSpan.appendChild(document.createTextNode('\u00A0'));
      saleSpan.appendChild(buildTaxLabel());
      priceP.appendChild(saleSpan);

      const compareSpan = document.createElement('span');
      compareSpan.className = 'products-grid__price--compare';
      compareSpan.dataset.compareAtPrice = String(rawCompare);
      // 元値も同様に cents → 表示金額に変換
      compareSpan.textContent = formatMoneyCents(rawCompare);
      compareSpan.appendChild(buildTaxLabel());
      priceP.appendChild(compareSpan);
    } else {
      /* 通常価格 */
      const regularSpan = document.createElement('span');
      regularSpan.className = 'products-grid__price--regular';
      regularSpan.dataset.price = String(rawPrice);
      // cents → 表示金額に変換（÷100 + カンマ区切り）
      regularSpan.appendChild(document.createTextNode(formatMoneyCents(rawPrice)));
      // セール価格と同様、NBSP で products-grid.liquid と同じ間隔を再現する
      regularSpan.appendChild(document.createTextNode('\u00A0'));
      regularSpan.appendChild(buildTaxLabel());
      priceP.appendChild(regularSpan);
    }

    link.appendChild(priceP);
    li.appendChild(link);

    /* ---- アクション（カート追加 + お気に入り）---- */
    const actions = document.createElement('div');
    actions.className = 'products-grid__actions';

    // カート追加フォーム（JS 無効でも動作するよう form/POST を使う）
    const form = document.createElement('form');
    form.method = 'post';
    form.action = cartAddUrl; // Liquid から受け取った安全な URL
    form.className = 'products-grid__add-to-cart';

    const inputId = document.createElement('input');
    inputId.type = 'hidden';
    inputId.name = 'id';
    inputId.value = variantId;

    const inputQty = document.createElement('input');
    inputQty.type = 'hidden';
    inputQty.name = 'quantity';
    inputQty.value = '1';

    const inputReturn = document.createElement('input');
    inputReturn.type = 'hidden';
    inputReturn.name = 'return_to';
    inputReturn.value = cartUrl; // Liquid から受け取った安全な URL

    const cartBtn = document.createElement('button');
    cartBtn.type = 'submit';
    cartBtn.className = 'products-grid__actions-cart';
    cartBtn.textContent = 'カートに追加';

    form.append(inputId, inputQty, inputReturn, cartBtn);
    actions.appendChild(form);

    // お気に入りボタン（ハートアイコン）
    // script_cart-actions.js が is-active / aria-pressed を管理するため初期値は false
    const favBtn = document.createElement('button');
    favBtn.className = 'products-grid__actions-favorite';
    favBtn.type = 'button';
    favBtn.setAttribute('aria-label', 'お気に入りから削除');
    favBtn.setAttribute('aria-pressed', 'false'); // syncAll() で正しい値に上書きされる

    // SVG はセキュリティ上 createElementNS で生成する（innerHTML 禁止）
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 640 640');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', HEART_SVG_PATH);
    svg.appendChild(path);
    favBtn.appendChild(svg);

    actions.appendChild(favBtn);
    li.appendChild(actions);

    return li;
  }

  /**
   * 価格表示の「円（税込）」ラベル要素を作成して返す。
   * buildProductCard の価格部分で共通利用する。
   */
  function buildTaxLabel() {
    const taxSpan = document.createElement('span');
    taxSpan.className = 'products-grid__tax';
    taxSpan.textContent = '円';
    const small = document.createElement('small');
    small.textContent = '（税込）';
    taxSpan.appendChild(small);
    return taxSpan;
  }

  /* ============================================================
     ページのDOM要素を取得するヘルパー
     ============================================================ */

  /** お気に入りページのルートセクション要素（cart_add_url などを保持） */
  function getSection() {
    return document.querySelector('.favorites-page');
  }

  /** 商品カードを挿入するリスト要素 */
  function getList() {
    return document.querySelector('.favorites-page__list');
  }

  /** 0件のときに表示するメッセージ要素 */
  function getEmptyMsg() {
    return document.querySelector('.favorites-page__empty');
  }

  /** 初回ロード中に表示するメッセージ要素 */
  function getLoadingMsg() {
    return document.querySelector('.favorites-page__loading');
  }

  /** 絞り込み結果が0件のときに表示するメッセージ要素 */
  function getFilterEmptyMsg() {
    return document.querySelector('.favorites-page__filter-empty');
  }

  /** 並び替え・絞り込みツールバー要素 */
  function getToolbar() {
    return document.querySelector('.favorites-page__toolbar');
  }

  /* ============================================================
     並び替え・絞り込みのUI状態を取得するヘルパー
     ============================================================ */

  /** 現在選択されている並び替え順を返す（'new' | 'old'） */
  function getSortOrder() {
    const select = document.querySelector('.favorites-page__sort-select');
    return select ? select.value : 'new';
  }

  /** 現在アクティブな絞り込み設定を返す（'all' | 'available' | 'sold-out'） */
  function getActiveFilter() {
    const btn = document.querySelector('.favorites-page__filter-btn.is-active');
    return btn ? (btn.dataset.filter || 'all') : 'all';
  }

  /* ============================================================
     並び替え・絞り込みロジック
     ============================================================ */

  /**
   * 商品にいずれか購入可能なバリアントが存在するか判定する。
   * buildProductCard でカード用バリアントを選ぶ基準と同じ（available が true のものが存在するか）。
   */
  function isProductAvailable(product) {
    return product.variants.some((v) => v.available);
  }

  /**
   * {entry, product} ペア配列に並び替えと絞り込みを適用して新しい配列を返す。
   * 元の配列は変更しない。
   *
   * @param {{ entry: Object, product: Object }[]} pairs
   * @param {'new'|'old'} sortOrder   - 'new'=新しい順、'old'=古い順
   * @param {'all'|'available'|'sold-out'} filterStatus - 絞り込み条件
   */
  function applySortAndFilter(pairs, sortOrder, filterStatus) {
    // 絞り込み: available は在庫あり、sold-out は在庫なし、all は全件
    let result = pairs;
    if (filterStatus === 'available') {
      result = pairs.filter(({ product }) => isProductAvailable(product));
    } else if (filterStatus === 'sold-out') {
      result = pairs.filter(({ product }) => !isProductAvailable(product));
    }

    // 並び替え: addedAt がない古いデータは 0 扱いにしてフォールバック
    return [...result].sort((a, b) => {
      const timeA = Number(a.entry.addedAt) || 0;
      const timeB = Number(b.entry.addedAt) || 0;
      return sortOrder === 'new' ? timeB - timeA : timeA - timeB;
    });
  }

  /* ============================================================
     お気に入り一覧を全件描画する（メイン処理）
     ============================================================ */

  /**
   * localStorage の v2 データを読み込み、各 handle で商品情報を取得して
   * products-grid と同じ構造のカードを生成・挿入する。
   *
   * 処理の流れ:
   *   1. localStorage から favorites v2 を読み込む
   *   2. 0件なら空状態メッセージを表示して終了する
   *   3. handle を使って最大 FETCH_CONCURRENCY 件ずつ商品 JSON を取得する
   *      （キャッシュがあれば再フェッチしない）
   *   4. 現在の並び替え・絞り込み設定を読み取って適用する
   *   5. 結果のカードを DOM に挿入する
   *   6. script_cart-actions.js の syncAll を起動してカート状態・お気に入り状態を確定させる
   */
  async function renderFavoritesPage() {
    const section = getSection();
    const list = getList();
    if (!section || !list) return;

    // Liquid が data 属性で渡したカートの URL を取得する
    const cartAddUrl = section.dataset.cartAddUrl || '/cart/add';
    const cartUrl = section.dataset.cartUrl || '/cart';

    // 再描画時に既存カードをクリアする（replaceChildren は innerHTML より安全）
    list.replaceChildren();

    // 各状態メッセージとツールバーへの参照を取得する
    const loadingMsg = getLoadingMsg();
    const emptyMsg = getEmptyMsg();
    const filterEmptyMsg = getFilterEmptyMsg();
    const toolbar = getToolbar();

    // ローディング開始: メッセージを表示して他を隠す
    if (loadingMsg) loadingMsg.hidden = false;
    if (emptyMsg) emptyMsg.hidden = true;
    if (filterEmptyMsg) filterEmptyMsg.hidden = true;

    const favorites = getFavoritesV2();

    if (favorites.length === 0) {
      // お気に入りが0件: 空状態メッセージを出してローディングとツールバーを隠す
      if (loadingMsg) loadingMsg.hidden = true;
      if (emptyMsg) emptyMsg.hidden = false;
      if (toolbar) toolbar.hidden = true;
      return;
    }

    // 各エントリの商品データを並列取得する（同時接続数を FETCH_CONCURRENCY に制限）
    const tasks = favorites.map((entry) => () => fetchProductByHandle(entry.handle));
    const products = await runWithConcurrency(tasks, FETCH_CONCURRENCY);

    // favorites と products を {entry, product} のペアにして、取得できたものだけ残す
    const pairs = favorites
      .map((entry, i) => ({ entry, product: products[i] }))
      .filter(({ product }) => product != null);

    // 現在の並び替え・絞り込み設定を読み取って適用する
    const filtered = applySortAndFilter(pairs, getSortOrder(), getActiveFilter());

    // 絞り込み後のカードを描画する
    filtered.forEach(({ product }) => {
      const card = buildProductCard(product, cartAddUrl, cartUrl);
      if (card) list.appendChild(card);
    });

    if (loadingMsg) loadingMsg.hidden = true;

    // お気に入りが1件以上あるのでツールバーを表示する
    if (toolbar) toolbar.hidden = false;

    if (list.children.length === 0) {
      // 絞り込みで0件になった場合はフィルター用の空メッセージを表示する
      if (getActiveFilter() !== 'all') {
        if (filterEmptyMsg) filterEmptyMsg.hidden = false;
      } else {
        // フィルターなしで0件 = 全商品が取得できなかったケース
        if (emptyMsg) emptyMsg.hidden = false;
        if (toolbar) toolbar.hidden = true;
      }
      return;
    }

    // script_cart-actions.js にカード状態の同期を依頼する
    // 'shopify:section:load' イベントを受けた script_cart-actions.js が syncAll() を実行し
    // カート追加済み判定・COMING SOON 判定・お気に入りボタンの is-active を確定させる
    document.dispatchEvent(new Event('shopify:section:load'));
  }

  /* ============================================================
     お気に入り変更イベントの処理
     ============================================================ */

  /**
   * script_cart-actions.js がお気に入りを変更したとき（同一タブ）または
   * 別タブで localStorage が更新されたときにページを再描画する。
   * キャッシュを活用するため、解除以外では追加フェッチは発生しない。
   */
  function onFavoritesChanged() {
    renderFavoritesPage();
  }

  /* 同一タブ内で script_cart-actions.js がお気に入り変更を通知する CustomEvent */
  document.addEventListener('roi:favorites:changed', onFavoritesChanged);

  /* 別タブ・別ウィンドウで localStorage が変更されたとき */
  window.addEventListener('storage', (e) => {
    if (e.key === FAVORITES_KEY) {
      onFavoritesChanged();
    }
  });

  /* BFCache（ブラウザの「戻る」）から復元されたときも再描画する */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      renderFavoritesPage();
    }
  });

  /* ============================================================
     初期化
     ============================================================ */

  /**
   * 並び替えセレクトが変わったら再描画する。
   * productCache が効いているため再フェッチは発生せず、表示順だけ変わる。
   */
  document.querySelector('.favorites-page__sort-select')
    ?.addEventListener('change', renderFavoritesPage);

  /**
   * 絞り込みボタンをクリックしたら is-active を切り替えてから再描画する。
   * productCache が効いているため再フェッチは発生せず、表示件数だけ変わる。
   */
  document.querySelectorAll('.favorites-page__filter-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      // 他のボタンの is-active を外して、クリックしたボタンだけアクティブにする
      document.querySelectorAll('.favorites-page__filter-btn')
        .forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      renderFavoritesPage();
    });
  });

  /**
   * DOMの準備状況に応じて描画タイミングを切り替える。
   * defer で読み込まれる場合は DOM 構築完了後に即実行される。
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderFavoritesPage);
  } else {
    renderFavoritesPage();
  }

})(); // IIFE 終了: グローバルスコープへの変数漏れを防ぐ
